// projectlab-web/src/components/ui/Button.tsx
import { forwardRef, ButtonHTMLAttributes, ElementType, ComponentPropsWithRef } from 'react'
import { clsx } from 'clsx'
import { Loader2 } from 'lucide-react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline'
type Size = 'xs' | 'sm' | 'md' | 'lg'

type ButtonOwnProps<E extends ElementType = 'button'> = {
  as?: E
  variant?: Variant
  size?: Size
  loading?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  fullWidth?: boolean
}

export type ButtonProps<E extends ElementType = 'button'> = ButtonOwnProps<E> &
  Omit<ComponentPropsWithRef<E>, keyof ButtonOwnProps<E>>

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-[var(--color-primary)] text-white border border-transparent hover:bg-[var(--color-primary-hover)] active:bg-[var(--color-primary-active)] shadow-sm hover:shadow-md',
  secondary:
    'bg-white text-[var(--color-text)] border border-[var(--color-border)] hover:bg-[var(--color-surface)] hover:border-[var(--color-border)] shadow-none',
  ghost:
    'bg-transparent text-[var(--color-text-muted)] border border-transparent hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]',
  danger:
    'bg-[var(--color-error)] text-white border border-transparent hover:opacity-92 active:opacity-85 shadow-sm',
  success:
    'bg-[var(--color-success)] text-white border border-transparent hover:opacity-92 active:opacity-85 shadow-sm',
  outline:
    'bg-transparent text-[var(--color-primary)] border border-[var(--color-border)] hover:bg-[var(--color-primary)]/[0.05] hover:border-[var(--color-primary)]/30',
}

const sizeClasses: Record<Size, string> = {
  xs: 'text-xs px-2.5 py-1.5 gap-1 rounded-lg min-h-[30px]',
  sm: 'text-sm px-4 py-2 gap-1.5 rounded-xl min-h-[38px]',
  md: 'text-sm px-5 py-2.5 gap-2 rounded-xl min-h-[46px]',
  lg: 'text-[15px] px-6 py-3 gap-2 rounded-[1rem] min-h-[54px]',
}

export const Button = forwardRef(
  <E extends ElementType = 'button'>(
    {
      as,
      variant = 'primary',
      size = 'md',
      loading,
      iconLeft,
      iconRight,
      fullWidth,
      className,
      children,
      disabled,
      type,
      ...props
    }: ButtonOwnProps<E> & Omit<ComponentPropsWithRef<E>, keyof ButtonOwnProps<E>>,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const Tag = as ?? 'button'
    const isDisabled = disabled || loading

    return (
      <Tag
        ref={ref}
        type={!as ? (type ?? 'button') : undefined}
        disabled={!as ? isDisabled : undefined}
        className={clsx(
          'inline-flex items-center justify-center font-semibold select-none whitespace-nowrap',
          'transition-all duration-[180ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)]',
          'disabled:opacity-45 disabled:cursor-not-allowed disabled:pointer-events-none',
          'active:scale-[0.99]',
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading ? (
          <Loader2
            size={size === 'xs' ? 12 : size === 'sm' ? 14 : 16}
            className="animate-spin shrink-0"
            aria-hidden
          />
        ) : iconLeft ? (
          <span className="shrink-0" aria-hidden>{iconLeft}</span>
        ) : null}

        {children ? <span>{children}</span> : null}

        {!loading && iconRight ? (
          <span className="shrink-0" aria-hidden>{iconRight}</span>
        ) : null}
      </Tag>
    )
  }
) as (<E extends ElementType = 'button'>(props: ButtonProps<E>) => React.ReactElement | null) & { displayName?: string }

Button.displayName = 'Button'